import { useEffect, useState } from 'react'
import { useDeliveryOrderLoaders, DeliveryOrder, DeliveryMethod, DeliveryStatus } from '../../../deliveries'
import { usePurchaseLoaders, PurchaseOrder } from '../../../purchases'
import { useSystemStateHooks } from '../../../system'
import { useManagerState } from '../../../manager'
import { isToday } from '../../utils/dashboardUtils'

export type WarehouseData = {
    retirosPendientes: DeliveryOrder[]
    enviosHoy: DeliveryOrder[]
    recepcionesPendientes: PurchaseOrder[]
}

// enabled: false when no visible card needs this data (it isn't loaded)
export const useWarehouseData = (enabled = true) => {
    const [loading, setLoading] = useState(enabled)
    const [error, setError] = useState(false)
    const [data, setData] = useState<WarehouseData | null>(null)

    const { companyId } = useSystemStateHooks()
    const { currentBranch } = useManagerState()
    const deliveryOrderLoaders = useDeliveryOrderLoaders()
    const purchaseLoaders = usePurchaseLoaders()

    useEffect(() => {
        if (!enabled) return
        setLoading(true)
        const load = async () => {
            try {
                const id = companyId
                const branchId = currentBranch?.id

                const branchFilter = branchId ? [{ prop: 'branchId', value: branchId }] : []

                const [pickupsRes, shipmentsRes, allPurchases] = await Promise.all([
                    deliveryOrderLoaders(id).loadDeliveryOrdersBatch({
                        valueFilters: [{ prop: 'method', value: DeliveryMethod.PICKUP }, ...branchFilter]
                    }),
                    deliveryOrderLoaders(id).loadDeliveryOrdersBatch({
                        valueFilters: [{ prop: 'method', value: DeliveryMethod.SHIPMENT }, ...branchFilter]
                    }),
                    purchaseLoaders(id).loadAllPurchases()
                ])

                const retirosPendientes = pickupsRes.dataTypes.filter(
                    d => !d.cancelled && isToday(d.deliveryDate) &&
                        (d.deliveryStatus === DeliveryStatus.PENDING || d.deliveryStatus === DeliveryStatus.PARTIALLY_DELIVERED)
                )

                const enviosHoy = shipmentsRes.dataTypes.filter(
                    d => !d.cancelled && isToday(d.deliveryDate)
                )

                const recepcionesPendientes = allPurchases.filter(p =>
                    !p.archived &&
                    (!branchId || p.deliveryBranchId === branchId) &&
                    (p.deliveryStatus === DeliveryStatus.PENDING || p.deliveryStatus === DeliveryStatus.PARTIALLY_DELIVERED)
                )

                setData({ retirosPendientes, enviosHoy, recepcionesPendientes })
            } catch {
                setError(true)
            } finally {
                setLoading(false)
            }
        }
        load()
    }, [enabled])

    return { loading, error, data }
}
